import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Pencil, Trash2, Search, Mail, ShieldCheck, ShieldOff } from 'lucide-react';
import AdminExportCsvButton from './AdminExportCsvButton';
import GiftCreditsButton from './GiftCreditsButton';
import { renderCellValue } from './adminHelpers';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];

const AdminDataTable = ({
  tab,
  activeTab,
  data,
  loading,
  search,
  setSearch,
  page,
  setPage,
  setEditing,
  onDelete,
  onVendorApprove,
  onCustomerStatus,
  onImpersonateVendor,
  onImpersonateCustomer,
  onSendWelcome,
  onSendWelcomeBulk,
  onBulkDelete,
  onBulkCustomerStatus,
  serverPaginated = false,
  totalRecords,
  pageSize,
  setPageSize,
}) => {
  const effectivePageSize = serverPaginated ? (pageSize || 50) : DEFAULT_PAGE_SIZE;
  const supportsWelcome = activeTab === 'suppliers' || activeTab === 'customers';
  const supportsBulkDelete = (['products', 'suppliers', 'customers'].includes(activeTab) && !tab.viewOnly) || activeTab === 'enquiries';
  const supportsBulkStatus = activeTab === 'customers' && !tab.viewOnly;
  const supportsSelection = supportsWelcome || supportsBulkDelete || supportsBulkStatus;
  const [selected, setSelected] = useState(() => new Set());
  // Reset selection whenever the active tab changes — official React pattern for
  // "derive state from a changing prop" (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
  const [prevTab, setPrevTab] = useState(activeTab);
  if (prevTab !== activeTab) {
    setPrevTab(activeTab);
    setSelected(new Set());
  }

  // In server-paginated mode the parent already applied `q` and slice — do NOT
  // filter or slice on the client. In client-paginated mode we keep the
  // existing full-text substring search over every field.
  const items = useMemo(() => {
    const arr = Array.isArray(data[activeTab]) ? data[activeTab] : [];
    if (serverPaginated || !search) return arr;
    const q = search.toLowerCase();
    return arr.filter((it) => JSON.stringify(it).toLowerCase().includes(q));
  }, [data, activeTab, search, serverPaginated]);

  const totalItems = serverPaginated ? (Number(totalRecords) || 0) : items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = useMemo(() => {
    if (serverPaginated) return items;
    return items.slice((currentPage - 1) * effectivePageSize, currentPage * effectivePageSize);
  }, [items, currentPage, serverPaginated, effectivePageSize]);

  // Column keys shown in header + body — derived once per items/tab change instead
  // of recomputing on every row render. Vendors/Customers get 8 columns so
  // contact details (mobile + email) stay visible.
  const maxCols = ['suppliers', 'customers'].includes(activeTab) ? 8 : 6;
  const columnKeys = useMemo(() => {
    const base = tab.fields || (items[0] ? Object.keys(items[0]) : []);
    return base.filter((k) => k !== 'id' && k !== 'images' && k !== 'specs').slice(0, maxCols);
  }, [tab.fields, items, maxCols]);

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePage = () => {
    setSelected((prev) => {
      const pageIds = pagedItems.map((it) => it.id);
      const allSelected = pageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const allPageSelected = pagedItems.length > 0 && pagedItems.every((it) => selected.has(it.id));

  const handleBulkWelcome = () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Send welcome email to ${ids.length} selected ${activeTab === 'suppliers' ? 'vendor' : 'customer'}(s)?`)) return;
    onSendWelcomeBulk?.(activeTab, ids, () => setSelected(new Set()));
  };

  const labelFor = () => ({ suppliers: 'vendor', customers: 'customer', enquiries: 'enquiry' }[activeTab] || 'product');
  const handleBulkDelete = () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} selected ${labelFor()}(s)? This cannot be undone.`)) return;
    onBulkDelete?.(activeTab, ids, () => setSelected(new Set()));
  };

  const handleBulkCustomerStatus = (status) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const verb = status === 'active' ? 'activate' : 'deactivate';
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${ids.length} selected customer(s)?`)) return;
    onBulkCustomerStatus?.(ids, status, () => setSelected(new Set()));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{tab.label}</h1>
          <p className="text-sm text-gray-600" data-testid={`${activeTab}-record-count`}>
            {totalItems} record{totalItems !== 1 ? 's' : ''}
            {serverPaginated && search && <> matching &ldquo;{search}&rdquo;</>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); if (!serverPaginated) setPage(1); }}
              placeholder={serverPaginated ? 'Search name / mobile / GST / city…' : 'Search...'}
              className="pl-8 w-64"
              data-testid={`${activeTab}-search-input`}
            />
          </div>
          {supportsBulkStatus && selected.size > 0 && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleBulkCustomerStatus('active')}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="bulk-activate-customers-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-1" /> Activate {selected.size} selected
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleBulkCustomerStatus('inactive')}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="bulk-deactivate-customers-btn"
              >
                <ShieldOff className="w-4 h-4 mr-1" /> Deactivate {selected.size} selected
              </Button>
            </>
          )}
          {supportsBulkDelete && selected.size > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={handleBulkDelete}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete {selected.size} selected
            </Button>
          )}
          {supportsWelcome && selected.size > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={handleBulkWelcome}
              className="border-[#2c54b2] text-[#2c54b2] hover:bg-blue-50"
              data-testid="bulk-send-welcome-btn"
            >
              <Mail className="w-4 h-4 mr-1" /> Send Welcome to {selected.size} selected
            </Button>
          )}
          {['products', 'suppliers', 'customers'].includes(activeTab) && (
            <AdminExportCsvButton resource={activeTab === 'suppliers' ? 'vendors' : activeTab} />
          )}
          {!tab.viewOnly && !tab.noCreate && (
            <Button
              onClick={() => {
                const initial = {};
                if (tab.fields?.includes('order')) {
                  const list = Array.isArray(data[activeTab]) ? data[activeTab] : [];
                  const maxOrder = list.reduce((m, d) => Math.max(m, Number(d.order) || 0), 0);
                  initial.order = maxOrder + 1;
                }
                setEditing(initial);
              }}
              className="bg-[#ee5e23] hover:bg-[#d24e15] text-white"
            >
              <Plus className="w-4 h-4 mr-1" /> Add New
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No records found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {supportsSelection && (
                    <th className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={togglePage}
                        aria-label="Select all on page"
                        data-testid="select-all-rows"
                      />
                    </th>
                  )}
                  {columnKeys.map((k) => (
                    <th key={k} className="px-4 py-2 text-left font-semibold text-gray-700 capitalize whitespace-nowrap">{k.replace(/_/g, ' ')}</th>
                  ))}
                  {!tab.viewOnly && <th className="px-4 py-2 w-24"></th>}
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((it) => (
                  <tr key={it.id} className={`border-b border-gray-100 hover:bg-gray-50 ${selected.has(it.id) ? 'bg-blue-50' : ''}`}>
                    {supportsSelection && (
                      <td className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={selected.has(it.id)}
                          onChange={() => toggleRow(it.id)}
                          aria-label="Select row"
                          data-testid={`select-row-${it.id}`}
                        />
                      </td>
                    )}
                    {columnKeys.map((k) => (
                      <td key={k} className="px-4 py-2 max-w-xs truncate">
                        {renderCellValue(it, k)}
                      </td>
                    ))}
                    {!tab.viewOnly && (
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {tab.key === 'suppliers' && (it.company_doc_url || it.gst_doc_url) && (
                          <div className="inline-flex gap-1 mr-2">
                            {it.company_doc_url && (
                              <a href={it.company_doc_url} target="_blank" rel="noreferrer"
                                className="px-2 py-1 text-xs bg-blue-50 text-[#2c54b2] hover:bg-blue-100 rounded transition-colors"
                                title="View company name proof">Co Doc</a>
                            )}
                            {it.gst_doc_url && (
                              <a href={it.gst_doc_url} target="_blank" rel="noreferrer"
                                className="px-2 py-1 text-xs bg-blue-50 text-[#2c54b2] hover:bg-blue-100 rounded transition-colors"
                                title="View GST certificate">GST Doc</a>
                            )}
                          </div>
                        )}
                        {tab.key === 'suppliers' && it.status !== 'active' && (
                          <button onClick={() => onVendorApprove(it.id, 'active')} className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 rounded transition-colors mr-1" title="Activate vendor">Activate</button>
                        )}
                        {tab.key === 'suppliers' && it.status === 'active' && (
                          <button onClick={() => onVendorApprove(it.id, 'inactive')} className="px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 rounded transition-colors mr-1" title="Deactivate vendor">Deactivate</button>
                        )}
                        {tab.key === 'customers' && (it.status === 'inactive' || !it.status) && (
                          <button onClick={() => onCustomerStatus(it.id, 'active')} className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 rounded transition-colors mr-1" title="Activate customer">Activate</button>
                        )}
                        {tab.key === 'customers' && it.status === 'active' && (
                          <button onClick={() => onCustomerStatus(it.id, 'inactive')} className="px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 rounded transition-colors mr-1" title="Deactivate customer">Deactivate</button>
                        )}
                        {tab.key === 'suppliers' && (
                          <button onClick={() => onImpersonateVendor(it)} className="px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded transition-colors mr-1" title="Open the vendor dashboard as this vendor" data-testid={`login-as-vendor-${it.id}`}>Login as</button>
                        )}
                        {tab.key === 'suppliers' && <GiftCreditsButton vendor={it} />}
                        {tab.key === 'customers' && (
                          <button onClick={() => onImpersonateCustomer(it)} className="px-2 py-1 text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded transition-colors mr-1" title="Open the buyer account as this customer" data-testid={`login-as-customer-${it.id}`}>Login as</button>
                        )}
                        {supportsWelcome && (
                          <button
                            onClick={() => onSendWelcome?.(activeTab, it)}
                            disabled={!it.email}
                            className="p-1.5 text-gray-600 hover:text-[#2c54b2] hover:bg-blue-50 rounded transition-colors disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                            title={it.email ? `Send welcome email to ${it.email}` : 'No email on file'}
                            data-testid={`send-welcome-${it.id}`}
                          >
                            <Mail className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => setEditing(it)} className="p-1.5 text-gray-600 hover:text-[#2c54b2] hover:bg-blue-50 rounded transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => onDelete(it.id)} className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {items.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-600 flex items-center gap-3 flex-wrap">
              <span>
                Showing {(currentPage - 1) * effectivePageSize + 1}
                –{Math.min(currentPage * effectivePageSize, totalItems)} of {totalItems}
                {supportsSelection && selected.size > 0 && <> · <b>{selected.size}</b> selected</>}
              </span>
              {serverPaginated && setPageSize && (
                <label className="flex items-center gap-1">
                  <span className="text-gray-500">Per page:</span>
                  <select
                    value={effectivePageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded px-1 py-0.5 text-xs bg-white"
                    data-testid="page-size-select"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="flex items-center gap-1" data-testid={`${activeTab}-pagination`}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-prev"
              >Prev</button>
              {Array.from({ length: totalPages }).map((_, i) => {
                const n = i + 1;
                if (totalPages > 7 && n !== 1 && n !== totalPages && Math.abs(n - currentPage) > 1) {
                  if (n === 2 || n === totalPages - 1) return <span key={n} className="px-1 text-gray-400">…</span>;
                  return null;
                }
                return (
                  <button
                    key={n}
                    onClick={() => setPage(n)}
                    className={`min-w-[28px] px-2 py-1 text-xs rounded transition-colors ${
                      n === currentPage
                        ? 'bg-[#ee5e23] text-white font-semibold'
                        : 'border border-gray-300 hover:bg-white text-gray-700'
                    }`}
                    data-testid={`pagination-page-${n}`}
                  >{n}</button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="pagination-next"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDataTable;
